import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import useAppStore from '../../store/app.store';
import useTema from '../../hooks/useTema';
import {
  Barra, LinhaTarefa, ModalNovaDemanda, ModalPassar, ModalPrazo,
  diaCurto, diaLongo, saudacao, primeiroNome,
} from './comum';

function Secao({ titulo, cor, children, t, vazio }) {
  return (
    <div style={{ marginTop: 18 }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: cor || t.textSecond, marginBottom: 8 }}>{titulo}</p>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden', marginTop: -1 }}>
        {vazio ? <p style={{ padding: '18px 14px', fontSize: 13, color: t.textMuted, textAlign: 'center' }}>{vazio}</p> : children}
      </div>
    </div>
  );
}

// usuarioId: quando o admin olha a pauta de outra pessoa (tela Equipe)
export default function Pauta({ usuarioId = null, embutida = false }) {
  const t = useTema();
  const eu = useAppStore((s) => s.usuario);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [novaAberta, setNovaAberta] = useState(false);
  const [passando, setPassando] = useState(null);
  const [mudandoPrazo, setMudandoPrazo] = useState(null);
  const [feitas, setFeitas] = useState(null);

  const carregar = useCallback(() => {
    api.get('/pauta', { params: usuarioId ? { usuario_id: usuarioId } : {} })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.error || 'Não foi possível carregar a pauta.'));
  }, [usuarioId]);

  useEffect(() => { setDados(null); setFeitas(null); carregar(); }, [carregar]);

  // Atualiza sozinha (demandas recebidas aparecem sem recarregar a página)
  useEffect(() => {
    const id = setInterval(carregar, 60000);
    return () => clearInterval(id);
  }, [carregar]);

  const concluir = async (x) => {
    const marcar = x.status !== 'concluida';
    try {
      await api.patch(`/pauta/${x.id}/concluir`, { concluida: marcar });
      carregar();
      if (feitas) verFeitas(true);
    } catch (e) { toast.error(e.response?.data?.error || 'Não foi possível atualizar.'); }
  };

  const remover = async (x) => {
    if (!window.confirm(`Remover "${x.titulo}" da pauta?`)) return;
    try { await api.delete(`/pauta/${x.id}`); toast.success('Removida.'); carregar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Não foi possível remover.'); }
  };

  const verFeitas = async (forcar = false) => {
    if (feitas && !forcar) { setFeitas(null); return; }
    const r = await api.get('/pauta/feitas', { params: usuarioId ? { usuario_id: usuarioId } : {} });
    setFeitas(r.data || []);
  };

  if (erro) return <p style={{ color: '#EF4444', padding: 20 }}>{erro}</p>;
  if (!dados) return <p style={{ color: t.textMuted, padding: 20 }}>Carregando a pauta…</p>;

  const { hoje, atrasadas, hoje_tarefas: doDia, andamento = [], proximas, usuario } = dados;
  const feitasHoje = doDia.filter((x) => x.status === 'concluida').length;
  const pendentesHoje = doDia.filter((x) => x.status !== 'concluida');
  const concluidasHoje = doDia.filter((x) => x.status === 'concluida');
  const minha = !usuarioId || usuarioId === eu?.id;

  const acoes = { hoje, t, onConcluir: concluir, onPassar: setPassando, onPrazo: setMudandoPrazo, onRemover: remover };

  // Próximos dias: só o que é novo. Sem rotinas diárias e sem a próxima repetição
  // de uma rotina que já está na pauta agora (ex.: a semanal da semana que vem)
  const rotinasAtuais = new Set([...atrasadas, ...pendentesHoje, ...andamento].map((x) => x.rotina_id).filter(Boolean));
  const proximasVisiveis = proximas.filter((x) => x.frequencia !== 'diaria' && !rotinasAtuais.has(x.rotina_id));
  const porDia = proximasVisiveis.reduce((acc, x) => { (acc[x.data_inicio] = acc[x.data_inicio] || []).push(x); return acc; }, {});

  return (
    <div style={{ maxWidth: 760, margin: embutida ? 0 : '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: embutida ? 18 : 22, fontWeight: 800, color: t.textTitle }}>
              {minha ? `${saudacao()}, ${primeiroNome(eu?.nome)}` : `Pauta de ${primeiroNome(usuario?.nome)}`}
            </h1>
            <p style={{ fontSize: 13, color: t.textMuted, marginTop: 2, textTransform: 'none' }}>
              {minha ? 'Sua pauta de ' : ''}{diaLongo(hoje)}{usuario?.cargo ? ` · ${usuario.cargo}` : ''}
            </p>
          </div>
          <button className="btn-primary" onClick={() => setNovaAberta(true)}>+ Nova demanda</button>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: t.textSecond, marginBottom: 6 }}>
            <span>Hoje: {feitasHoje} de {doDia.length} feitas</span>
            <span style={{ fontWeight: 700 }}>{doDia.length ? Math.round((feitasHoje / doDia.length) * 100) : 0}%</span>
          </div>
          <Barra feitas={feitasHoje} total={doDia.length} t={t} />
        </div>
        {usuario?.indicador && (
          <p style={{ fontSize: 12, color: t.textMuted, marginTop: 12 }}>
            🎯 Indicador do mês: <span style={{ color: t.textSecond }}>{usuario.indicador}</span>
          </p>
        )}
      </div>

      {atrasadas.length > 0 && (
        <Secao titulo={`⚠️ Atrasadas (${atrasadas.length})`} cor="#EF4444" t={t}>
          {atrasadas.map((x) => <LinhaTarefa key={x.id} tarefa={x} {...acoes} />)}
        </Secao>
      )}

      <Secao titulo={`Hoje (${pendentesHoje.length} para fazer)`} t={t}
        vazio={doDia.length === 0 ? (andamento.length ? 'Nada vence hoje. Adiante o que está em andamento.' : 'Nada para hoje. Se surgir algo, use “Nova demanda”.') : null}>
        {pendentesHoje.map((x) => <LinhaTarefa key={x.id} tarefa={x} {...acoes} />)}
        {pendentesHoje.length === 0 && doDia.length > 0 && (
          <p style={{ padding: '14px', fontSize: 13, color: '#22C55E', textAlign: 'center', fontWeight: 600 }}>Tudo feito por hoje! 🎉</p>
        )}
        {concluidasHoje.map((x) => <LinhaTarefa key={x.id} tarefa={x} {...acoes} />)}
      </Secao>

      {andamento.length > 0 && (
        <Secao titulo={`Em andamento (${andamento.length}) · prazo mais à frente`} t={t}>
          {andamento.map((x) => <LinhaTarefa key={x.id} tarefa={x} {...acoes} />)}
        </Secao>
      )}

      <Secao titulo="Próximos dias" t={t} vazio={proximasVisiveis.length === 0 ? 'Nada novo programado para os próximos 7 dias.' : null}>
        {Object.entries(porDia).map(([dia, lista]) => (
          <div key={dia}>
            <p style={{ padding: '10px 14px 2px', fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '.06em', borderTop: `1px solid ${t.border}` }}>
              {diaCurto(dia)}
            </p>
            {lista.map((x) => <LinhaTarefa key={x.id} tarefa={x} {...acoes} compacta />)}
          </div>
        ))}
      </Secao>

      <div style={{ marginTop: 18 }}>
        <button onClick={() => verFeitas()} style={{ fontSize: 13, color: t.textSecond, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {feitas ? '▾' : '▸'} {minha ? 'O que eu já fiz este mês' : 'O que já foi feito este mês'}
        </button>
        {feitas && (
          <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, marginTop: 8, overflow: 'hidden' }}>
            {feitas.length === 0 && <p style={{ padding: 14, fontSize: 13, color: t.textMuted, textAlign: 'center' }}>Nada concluído ainda este mês.</p>}
            {feitas.map((x) => {
              const noPrazo = x.concluida_dia <= x.prazo;
              return (
                <div key={x.id} style={{ display: 'flex', gap: 10, padding: '9px 14px', borderTop: `1px solid ${t.border}`, fontSize: 13 }}>
                  <span style={{ color: '#22C55E' }}>✓</span>
                  <span style={{ flex: 1, color: t.text }}>{x.titulo}</span>
                  <span style={{ color: noPrazo ? t.textMuted : '#F59E0B', whiteSpace: 'nowrap' }}>
                    {diaCurto(x.concluida_dia)}{noPrazo ? '' : ' · com atraso'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModalNovaDemanda aberto={novaAberta} onFechar={() => setNovaAberta(false)} hoje={hoje}
        usuarioAtual={eu} paraQuem={usuarioId || eu?.id}
        onCriada={() => { setNovaAberta(false); carregar(); }} />
      <ModalPassar tarefa={passando} onFechar={() => setPassando(null)} onPassada={() => { setPassando(null); carregar(); }} />
      <ModalPrazo tarefa={mudandoPrazo} hoje={hoje} onFechar={() => setMudandoPrazo(null)} onSalvo={() => { setMudandoPrazo(null); carregar(); }} />
    </div>
  );
}
